import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Languages, ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { OVERLAY_STRINGS } from "../i18n/overlayStrings";

export const TranslationOverlay = () => {
  const { translating, lang, languages } = useLang();
  const current = languages.find((l) => l.code === lang);
  const ui = OVERLAY_STRINGS[lang] || OVERLAY_STRINGS.en;

  return (
    <AnimatePresence>
      {translating && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          data-testid="translation-status"
        >
          {/* top indeterminate progress bar */}
          <div className="absolute top-0 left-0 right-0 h-[3px] overflow-hidden" data-testid="translation-progressbar">
            <span className="indeterminate" />
          </div>

          <motion.div
            initial={{ scale: 0.92, y: 12 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.92, y: 12 }}
            transition={{ duration: 0.25 }}
            className="glass border border-[#00F0FF]/30 rounded-3xl px-8 py-9 w-[calc(100vw-3rem)] max-w-md text-center shadow-2xl"
          >
            <div className="relative mx-auto w-16 h-16 mb-6">
              <span className="absolute inset-0 rounded-2xl bg-[#00F0FF]/10 border border-[#00F0FF]/40 pulse-ring" />
              <span className="absolute inset-0 flex items-center justify-center">
                <Languages size={30} className="text-cyan" />
              </span>
            </div>

            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan mb-3">
              {ui.label}
            </div>
            <h3 className="font-head font-bold text-2xl text-white mb-6">
              {ui.title}
            </h3>

            {/* source -> target */}
            <div className="flex items-center justify-center gap-3 mb-7">
              <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-300">
                🇬🇧 English
              </span>
              <ArrowRight size={18} className="text-cyan shrink-0" />
              <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/40 text-sm text-white font-medium">
                {current ? `${current.flag} ${current.native}` : "…"}
              </span>
            </div>

            {/* progress track */}
            <div className="relative h-1.5 w-full rounded-full bg-white/10 overflow-hidden mb-4">
              <span className="indeterminate" />
            </div>

            <p className="text-sm text-zinc-400">
              {ui.caption}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
